import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Tag, Percent, Trash2, Edit2, Calendar, ArrowLeft, AlignLeft, Layers, DollarSign, ShoppingCart, Gift, Activity, Power, ChevronLeft, ChevronRight } from 'lucide-react';
import { CustomSelect } from '../../components/CustomSelect';
import { CustomDatePickerFixed } from '../../components/CustomDatePickerFixed';
import { fetchWithAuth } from '../../utils/api';
import { IconAction, RowActions } from '../../components/RowActions';
import { StatusToggle } from '../../components/StatusToggle';


const Field = ({ label, icon: Icon, error, required, children }) => {
  const isTextarea = children?.type === 'textarea';
  const isCustomComponent = children?.type === CustomSelect || children?.type === CustomDatePickerFixed || children?.props?.isCustomSelect;
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </span>
      <span className="relative block">
        {Icon && (
          <span className={`absolute left-3 ${isTextarea ? 'top-3' : 'top-1/2 -translate-y-1/2'} z-[60] ${error ? 'text-rose-400' : 'text-slate-400'}`} aria-hidden="true">
            <Icon size={14} />
          </span>
        )}
        <span className="block">{React.cloneElement(children, {
          error: !!error,
          className: isCustomComponent
            ? `${children.props.className || ''} ${error ? 'border-rose-400 bg-rose-50 focus:border-rose-500' : ''} ${Icon ? 'pl-9' : ''}`
            : `${children.props.className || ''} w-full rounded-lg bg-gray-50 border ${error ? 'border-rose-400 focus:border-rose-500' : 'border-gray-100 focus:border-[var(--theme-primary)]'} focus:bg-white outline-none px-3 py-2 text-xs font-medium text-[#0d0d0d] transition-colors ${Icon ? 'pl-9' : ''}`,
        })}</span>
      </span>
      {error && <p className="text-[11px] font-medium text-rose-500 mt-1">{error}</p>}
    </label>
  );
};

export const OffersDiscountsTab = ({ org, showNotification }) => {
  const [activeTab, setActiveTab] = useState('offers'); // 'offers' or 'discounts'
  const [searchTerm, setSearchTerm] = useState('');
  
  const [offers, setOffers] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination State
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalOffers, setTotalOffers] = useState(0);
  const [totalDiscounts, setTotalDiscounts] = useState(0);

  // Views: 'list', 'offer_form', 'discount_form'
  const [viewState, setViewState] = useState('list');
  const [editItem, setEditItem] = useState(null);

  // Forms state
  const [offerForm, setOfferForm] = useState({ 
    name: '', description: '', offerType: 'PERCENTAGE', discountVal: '', 
    triggerId: '', triggerType: 'SERVICE', rewardId: '', rewardType: 'SERVICE', 
    overridePrice: '', validDays: [], validFrom: '', validUntil: '', status: 'active' 
  });
  const [errors, setErrors] = useState({});
  const [discountForm, setDiscountForm] = useState({ 
    code: '', name: '', type: 'percentage', value: '', 
    appliesTo: 'all_plans', duration: 'once', 
    maxRedemptions: '', validFrom: '', validUntil: '', status: 'active' 
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [oRes, dRes, sRes, pRes] = await Promise.all([
        fetchWithAuth(`/offers?page=${page}&limit=${perPage}`, { headers: { 'x-org-id': org.id } }),
        fetchWithAuth(`/discounts?page=${page}&limit=${perPage}`, { headers: { 'x-org-id': org.id } }),
        fetchWithAuth('/services', { headers: { 'x-org-id': org.id } }),
        fetchWithAuth('/products', { headers: { 'x-org-id': org.id } })
      ]);
      const oData = await oRes.json();
      const dData = await dRes.json();
      const sData = await sRes.json();
      const pData = await pRes.json();
      
      setTotalOffers(parseInt(oRes.headers.get('X-Total-Count') || oData?.length || 0, 10));
      setTotalDiscounts(parseInt(dRes.headers.get('X-Total-Count') || dData?.length || 0, 10));

      setOffers(oData || []);
      setDiscounts(dData || []);
      setServices(sData || []);
      setProducts(pData || []);
    } catch (err) {
      console.error(err);
      showNotification('Failed to fetch data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Refetch when org or pagination changes
  useEffect(() => {
    if (org?.id) {
      fetchData();
    }
  }, [org?.id, page, perPage, activeTab]);

  // Reset page to 1 when changing tabs
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const handleSaveOffer = async (e) => {
    e.preventDefault();
    try {
      const newErrors = {};
      if (!offerForm.name.trim()) newErrors.name = 'Please enter an offer name.';
      if (offerForm.offerType === 'BOGO') {
        if (!offerForm.triggerId) newErrors.triggerId = 'Please select a trigger service.';
        if (!offerForm.rewardId) newErrors.rewardId = 'Please select a reward service.';
      }
      if (offerForm.offerType === 'FIXED_PRICE') {
        if (!offerForm.triggerId) newErrors.triggerId = 'Please select a target service.';
        if (!offerForm.overridePrice) newErrors.overridePrice = 'Please enter a promotional price.';
      }
      if ((offerForm.offerType === 'PERCENTAGE' || offerForm.offerType === 'FLAT_AMOUNT') && !offerForm.discountVal) {
        newErrors.discountVal = 'Please enter a valid discount value.';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return showNotification('Please fix the highlighted fields.', 'error');
      }
      setErrors({});

      const payload = {
        name: offerForm.name,
        description: offerForm.description,
        offerType: offerForm.offerType,
        discountVal: parseFloat(offerForm.discountVal) || 0,
        triggerId: offerForm.triggerId,
        triggerType: offerForm.triggerType,
        rewardId: offerForm.rewardId,
        rewardType: offerForm.rewardType,
        overridePrice: parseFloat(offerForm.overridePrice) || 0,
        validDays: JSON.stringify(offerForm.validDays),
        status: offerForm.status
      };
      
      // Only attach dates if they are non-empty strings
      if (offerForm.validFrom && offerForm.validFrom.trim() !== '') {
        payload.validFrom = offerForm.validFrom;
      }
      if (offerForm.validUntil && offerForm.validUntil.trim() !== '') {
        payload.validUntil = offerForm.validUntil;
      }

      if (editItem) {
        const res = await fetchWithAuth(`/offers/${editItem.id}`, {
          method: 'PUT',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to update offer');
        showNotification('Offer updated successfully.', 'success');
      } else {
        const res = await fetchWithAuth('/offers', {
          method: 'POST',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to create offer');
        showNotification('Offer created successfully.', 'success');
      }
      setViewState('list');
      setEditItem(null);
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification('Error saving offer.', 'error');
    }
  };

  const handleSaveDiscount = async (e) => {
    e.preventDefault();
    try {
      const newErrors = {};
      if (!discountForm.code || !discountForm.code.trim()) newErrors.code = 'Please enter a promo code.';
      if (!discountForm.value) newErrors.value = 'Please enter a discount value.';
      if (!discountForm.validFrom) newErrors.validFrom = 'Start date is required.';

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return showNotification('Please fix the highlighted fields.', 'error');
      }
      setErrors({});

      const payload = {
        name: discountForm.name || discountForm.code, // use code as name for basic discount
        type: discountForm.type,
        value: Number(discountForm.value),
        status: discountForm.status || 'active'
      };
      if (discountForm.validFrom) payload.validFrom = discountForm.validFrom;
      if (discountForm.validUntil) payload.validUntil = discountForm.validUntil;

      if (editItem) {
        await fetchWithAuth(`/discounts/${editItem.id}`, {
          method: 'PUT',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        showNotification('Discount updated successfully.', 'success');
      } else {
        await fetchWithAuth('/discounts', {
          method: 'POST',
          headers: { 'x-org-id': org.id, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        showNotification('Discount created successfully.', 'success');
      }
      setViewState('list');
      setEditItem(null);
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification('Error saving discount.', 'error');
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return;
    try {
      await fetchWithAuth(`/${type}s/${id}`, {
        method: 'DELETE',
        headers: { 'x-org-id': org.id }
      });
      showNotification(`${type} deleted successfully.`, 'success');
      fetchData();
    } catch (err) {
      console.error(err);
      showNotification(`Failed to delete ${type}.`, 'error');
    }
  };

  const handleToggleOfferStatus = async (offer) => {
    const newStatus = offer.status === 'active' ? 'inactive' : 'active';
    try {
      const updatedOffer = { ...offer, status: newStatus };
      await fetchWithAuth(`/offers/${offer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-org-id': org.id },
        body: JSON.stringify(updatedOffer)
      });
      setOffers(offers.map(o => o.id === offer.id ? updatedOffer : o));
      showNotification(`Campaign marked as ${newStatus}.`, 'success');
    } catch (err) {
      console.error(err);
      showNotification('Failed to update status', 'error');
    }
  };

  const handleToggleDiscountStatus = async (disc) => {
    const newStatus = disc.status === 'active' ? 'inactive' : 'active';
    try {
      // Create a payload that satisfies the Go struct backend
      const updatedDisc = { 
        ...disc, 
        status: newStatus 
      };
      // Note: Backend might not support updating status for discounts yet, but we try:
      await fetchWithAuth(`/discounts/${disc.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-org-id': org.id },
        body: JSON.stringify(updatedDisc)
      });
      setDiscounts(discounts.map(d => d.id === disc.id ? updatedDisc : d));
      showNotification(`Promo code marked as ${newStatus}.`, 'success');
    } catch (err) {
      console.error(err);
      showNotification('Failed to update status', 'error');
    }
  };

  const openEditOffer = (offer) => {
    setEditItem(offer);
    let vDays = [];
    try { if (offer.validDays) vDays = JSON.parse(offer.validDays); } catch (e) {}
    
    setOfferForm({
      name: offer.name || '',
      description: offer.description || '',
      offerType: offer.offerType || 'PERCENTAGE',
      discountVal: offer.discountVal || 0,
      triggerId: offer.triggerId || '',
      triggerType: offer.triggerType || 'SERVICE',
      rewardId: offer.rewardId || '',
      rewardType: offer.rewardType || 'SERVICE',
      overridePrice: offer.overridePrice || 0,
      validDays: vDays,
      validFrom: offer.validFrom ? offer.validFrom.split('T')[0] : '',
      validUntil: offer.validUntil ? offer.validUntil.split('T')[0] : '',
      status: offer.status || 'active'
    });
    setViewState('offer_form');
  };

  const openEditDiscount = (disc) => {
    setEditItem(disc);
    setDiscountForm({
      code: disc.name || disc.code || '',
      name: disc.name || '',
      type: disc.type || 'percentage',
      value: disc.value || '',
      appliesTo: disc.appliesTo || 'all_plans',
      duration: disc.duration || 'once',
      maxRedemptions: disc.maxRedemptions || '',
      validFrom: disc.validFrom ? disc.validFrom.split('T')[0] : '',
      validUntil: disc.validUntil ? disc.validUntil.split('T')[0] : '',
      status: disc.status || 'active'
    });
    setViewState('discount_form');
  };

  const pageOffers = offers.filter(o => o.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const pageDiscounts = discounts.filter(d => d.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const activeList = activeTab === 'offers' ? pageOffers : pageDiscounts;
  
  // Calculate total pages from backend total count
  const totalRecords = activeTab === 'offers' ? totalOffers : totalDiscounts;
  const totalPages = Math.max(1, Math.ceil(totalRecords / perPage));

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      {viewState === 'list' && (
        <>
          {/* Action Header Section */}
          <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg px-5 py-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 shadow-sm">
            <div className="relative flex-1 min-w-[180px] max-w-lg">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="search"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setEditItem(null);
                if (activeTab === 'offers') {
                  setOfferForm({ 
                    name: '', description: '', offerType: 'PERCENTAGE', discountVal: '', 
                    triggerId: '', triggerType: 'SERVICE', rewardId: '', rewardType: 'SERVICE', 
                    overridePrice: '', validDays: [], validFrom: '', validUntil: '', status: 'active' 
                  });
                  setViewState('offer_form');
                } else {
                  setDiscountForm({ 
                    code: '', name: '', type: 'percentage', value: '', 
                    appliesTo: 'all_plans', duration: 'once', 
                    maxRedemptions: '', validFrom: '', validUntil: '', status: 'active' 
                  });
                  setViewState('discount_form');
                }
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] transition-colors shadow-lg shrink-0"
              style={{ background: 'var(--theme-primary)', boxShadow: '0 12px 24px -12px var(--theme-primary)' }}
            >
              <Plus size={14} aria-hidden="true" /> New {activeTab === 'offers' ? 'Offer' : 'Discount'}
            </button>
          </section>

          {/* Enterprise Tabs */}
          <div className="border-b border-[var(--theme-tertiary)] mb-6">
            <nav className="flex space-x-6 px-2" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('offers')}
                  className={`flex items-center py-4 px-4 text-xs font-bold tracking-wider uppercase border-b-2 transition-colors ${
                    activeTab === 'offers'
                      ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  ACTIVE OFFERS
                </button>
                <button
                  onClick={() => setActiveTab('discounts')}
                  className={`flex items-center py-4 px-4 text-xs font-bold tracking-wider uppercase border-b-2 transition-colors ${
                    activeTab === 'discounts'
                      ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  PROMO CODES
                </button>
              </nav>
            </div>

            {/* Content Area */}
          {loading ? (
            <div className="flex justify-center py-20 items-center">
              <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
            </div>
          ) : activeTab === 'offers' ? (
            <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden relative shadow-sm flex flex-col min-h-0 w-full flex-1">
              <div className="overflow-auto overscroll-none flex-1 custom-scrollbar min-h-0 table-scrollbar">
              <table className="w-full text-left border-collapse relative">
                <thead className="bg-white sticky top-0 z-10 shadow-[0_1px_0_0_var(--theme-tertiary)]">
                  <tr>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[20%]">Offer Name</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%]">Type</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[20%]">Details</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%]">Expiry</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-center w-[12%]">Status</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right w-[14%]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--theme-tertiary)]">
                  {pageOffers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-12 text-center text-gray-500">
                        No offers found matching your criteria.
                      </td>
                    </tr>
                  ) : (
                    pageOffers.map(offer => (
                      <tr key={offer.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors group">
                        <td className="px-5 py-3 overflow-hidden">
                          <p className="text-[13px] font-extrabold text-[#0d0d0d] uppercase tracking-wide truncate" title={offer.name}>{offer.name}</p>
                        </td>
                        <td className="px-5 py-3 overflow-hidden">
                          <p className="text-[12px] font-semibold text-slate-700 capitalize truncate">{offer.offerType === 'FLAT_AMOUNT' ? 'Flat Amount' : offer.offerType === 'FIXED_PRICE' ? 'Fixed Price' : offer.offerType.toLowerCase()}</p>
                        </td>
                        <td className="px-5 py-3 overflow-hidden">
                          {offer.offerType === 'PERCENTAGE' && <p className="text-[12px] font-medium text-slate-500 truncate">{offer.discountVal}% OFF</p>}
                          {offer.offerType === 'FLAT_AMOUNT' && <p className="text-[12px] font-medium text-slate-500 truncate">₹{offer.discountVal} OFF</p>}
                          {offer.offerType === 'BOGO' && <p className="text-[12px] font-medium text-slate-500 truncate">Buy 1 Get 1 FREE</p>}
                          {offer.offerType === 'FIXED_PRICE' && <p className="text-[12px] font-medium text-slate-500 truncate">Special Price ₹{offer.overridePrice}</p>}
                        </td>
                        <td className="px-5 py-3 overflow-hidden">
                          <p className="text-[12px] font-medium text-slate-500 truncate">{offer.validUntil ? new Date(offer.validUntil).toLocaleDateString() : 'No Expiry'}</p>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <div className="flex justify-center">
                            <StatusToggle
                              on={offer.status === 'active'}
                              label={offer.name}
                              onChange={() => handleToggleOfferStatus(offer)}
                            />
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <RowActions>
                            <IconAction icon={Edit2} label={`Edit ${offer.name}`} title="Edit" onClick={() => openEditOffer(offer)} />
                            <IconAction icon={Trash2} tone="danger" label={`Remove ${offer.name}`} title="Remove" onClick={() => handleDelete('offer', offer.id)} />
                          </RowActions>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </section>
          ) : (
            <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg overflow-hidden relative shadow-sm flex flex-col min-h-0 w-full flex-1">
              <div className="overflow-auto overscroll-none flex-1 custom-scrollbar min-h-0 table-scrollbar">
              <table className="w-full text-left border-collapse relative">
                <thead className="bg-white sticky top-0 z-10 shadow-[0_1px_0_0_var(--theme-tertiary)]">
                  <tr>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[20%]">Code Name</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%]">Type</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%]">Value</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 w-[15%]">Applies To</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-center w-[12%]">Status</th>
                    <th className="px-5 py-3 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 text-right w-[14%]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--theme-tertiary)]">
                  {pageDiscounts.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-5 py-12 text-center text-gray-500">
                        No promo codes found matching your criteria.
                      </td>
                    </tr>
                  ) : (
                    pageDiscounts.map(disc => (
                      <tr key={disc.id} className="border-b border-[var(--theme-tertiary)] last:border-0 hover:bg-slate-50/50 transition-colors group">
                        <td className="px-5 py-3 overflow-hidden">
                          <p className="text-[13px] font-extrabold text-[#0d0d0d] uppercase tracking-wide truncate" title={disc.name}>{disc.name}</p>
                        </td>
                        <td className="px-5 py-3 overflow-hidden">
                          <p className="text-[12px] font-semibold text-slate-700 capitalize truncate">{disc.type}</p>
                        </td>
                        <td className="px-5 py-3 overflow-hidden">
                          <p className="text-[12px] font-medium text-slate-500 truncate">
                            {disc.type === 'percentage' ? `${disc.value}%` : `₹${disc.value}`}
                          </p>
                        </td>
                        <td className="px-5 py-3 overflow-hidden">
                          <p className="text-[12px] font-medium text-slate-500 truncate">{disc.appliesTo === 'all_plans' ? 'All Plans' : 'Specific Plan'}</p>
                        </td>
                        <td className="px-5 py-3 text-center">
                          <div className="flex justify-center">
                            <StatusToggle
                              on={disc.status === 'active'}
                              label={disc.name}
                              onChange={() => handleToggleDiscountStatus(disc)}
                            />
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <RowActions>
                            <IconAction icon={Edit2} label={`Edit ${disc.name}`} title="Edit" onClick={() => openEditDiscount(disc)} />
                            <IconAction icon={Trash2} tone="danger" label={`Remove ${disc.name}`} title="Remove" onClick={() => handleDelete('discount', disc.id)} />
                          </RowActions>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              </div>
            </section>
          )}

          {totalPages > 1 && (
            <section className="px-5 py-3 flex items-center justify-between text-[11px] font-bold text-slate-500 border-t border-[var(--theme-tertiary)] bg-white shrink-0">
              <div className="flex items-center gap-2">
                <span className="uppercase tracking-widest">Showing</span>
                <CustomSelect
                  value={perPage}
                  onChange={(val) => {
                    setPerPage(Number(val));
                    setPage(1);
                  }}
                  options={[
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' },
                    { value: 100, label: '100' },
                    { value: activeList.length > 0 ? activeList.length : 200, label: 'All' }
                  ]}
                  className="w-16 bg-white border border-[var(--theme-tertiary)] rounded-sm px-2 py-1 text-[11px] font-bold text-slate-700 outline-none hover:border-[var(--theme-primary)] transition-colors cursor-pointer"
                  menuUp={true}
                />
              </div>
              <div className="flex items-center gap-4">
                <span className="uppercase tracking-widest">
                  PAGE <strong className="text-slate-800 mx-0.5">{page}</strong> OF <strong className="text-slate-800 ml-0.5">{totalPages}</strong>
                </span>
                <div className="flex items-center gap-1.5">
                  <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                    <ChevronLeft size={14} />
                  </button>
                  <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="w-8 h-8 rounded-md bg-white border border-[var(--theme-tertiary)] flex items-center justify-center text-slate-500 hover:text-[var(--theme-primary)] hover:border-[var(--theme-primary)] disabled:opacity-40 disabled:hover:border-[var(--theme-tertiary)] disabled:hover:text-slate-500 transition-all shadow-sm">
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* Offer Inline Form */}
      <AnimatePresence mode="wait">
        {viewState === 'offer_form' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="flex items-center gap-4 px-8 py-6 border-b border-gray-100 bg-gray-50/50">
              <button onClick={() => setViewState('list')} className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors shadow-sm shrink-0">
                <ArrowLeft size={16} aria-hidden="true" />
              </button>
              <div>
                <h3 className="text-xl font-bold text-gray-900 tracking-tight">{editItem ? 'Edit Offer Campaign' : 'Create New Offer Campaign'}</h3>
                <p className="text-sm text-gray-500 mt-1 font-medium">Define rules, discounts, and rewards for this campaign.</p>
              </div>
            </div>
            <form onSubmit={handleSaveOffer} className="p-8 space-y-6" noValidate>
                <Field label="Offer Name" icon={Tag} error={errors.name} required>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Summer Special"
                    value={offerForm.name}
                    onChange={e => {
                      setOfferForm({ ...offerForm, name: e.target.value });
                      if (errors.name) setErrors({ ...errors, name: undefined });
                    }}
                  />
                </Field>
                <Field label="Description" icon={AlignLeft}>
                  <textarea
                    rows="3"
                    value={offerForm.description}
                    onChange={e => setOfferForm({ ...offerForm, description: e.target.value })}
                    className="resize-none"
                  ></textarea>
                </Field>
                <Field label="Offer Type" icon={Layers}>
                  <CustomSelect
                    value={offerForm.offerType}
                    onChange={v => setOfferForm({ ...offerForm, offerType: v })}
                    options={[
                      { value: "PERCENTAGE", label: "Percentage Off (%)" },
                      { value: "FLAT_AMOUNT", label: "Flat Amount Off (₹)" },
                      { value: "BOGO", label: "Buy X, Get Y Free" },
                      { value: "FIXED_PRICE", label: "Fixed Promotional Price" }
                    ]}
                  />
                </Field>

                {(offerForm.offerType === 'PERCENTAGE' || offerForm.offerType === 'FLAT_AMOUNT') && (
                  <Field label="Discount Value" icon={offerForm.offerType === 'PERCENTAGE' ? Percent : DollarSign} error={errors.discountVal} required>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={offerForm.discountVal}
                      onChange={e => {
                        setOfferForm({ ...offerForm, discountVal: e.target.value });
                        if (errors.discountVal) setErrors({ ...errors, discountVal: undefined });
                      }}
                      placeholder={offerForm.offerType === 'PERCENTAGE' ? "e.g. 10" : "e.g. 50"}
                    />
                  </Field>
                )}

                {offerForm.offerType === 'BOGO' && (
                  <div className="grid grid-cols-2 gap-4 bg-blue-50/30 p-5 rounded-2xl border border-blue-100">
                    <Field label="Customer Buys (Trigger)" icon={ShoppingCart} error={errors.triggerId}>
                      <CustomSelect
                        value={offerForm.triggerId}
                        onChange={v => {
                          setOfferForm({ ...offerForm, triggerId: v });
                          if (errors.triggerId) setErrors({ ...errors, triggerId: undefined });
                        }}
                        options={[
                          { value: "", label: "Select Service" },
                          ...services.map(s => ({ value: s.id, label: s.name }))
                        ]}
                      />
                    </Field>
                    <Field label="Gets Free (Reward)" icon={Gift} error={errors.rewardId}>
                      <CustomSelect
                        value={offerForm.rewardId}
                        onChange={v => {
                          setOfferForm({ ...offerForm, rewardId: v });
                          if (errors.rewardId) setErrors({ ...errors, rewardId: undefined });
                        }}
                        options={[
                          { value: "", label: "Select Service" },
                          ...services.map(s => ({ value: s.id, label: s.name }))
                        ]}
                      />
                    </Field>
                  </div>
                )}

                {offerForm.offerType === 'FIXED_PRICE' && (
                  <div className="space-y-5 bg-amber-50/30 p-5 rounded-2xl border border-amber-100">
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Target Item" icon={Layers} error={errors.triggerId}>
                        <CustomSelect
                          value={offerForm.triggerId}
                          onChange={v => {
                            setOfferForm({ ...offerForm, triggerId: v });
                            if (errors.triggerId) setErrors({ ...errors, triggerId: undefined });
                          }}
                          options={[
                            { value: "", label: "Select Service" },
                            ...services.map(s => ({ value: s.id, label: s.name }))
                          ]}
                        />
                      </Field>
                      <Field label="Promotional Price (₹)" icon={DollarSign} error={errors.overridePrice}>
                        <input
                          type="number"
                          step="0.01"
                          placeholder="e.g. 499"
                          value={offerForm.overridePrice}
                          onChange={e => {
                            setOfferForm({ ...offerForm, overridePrice: e.target.value });
                            if (errors.overridePrice) setErrors({ ...errors, overridePrice: undefined });
                          }}
                        />
                      </Field>
                    </div>
                    <label className="block space-y-1">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-600">Valid Days</span>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                          <label key={day} className="flex items-center gap-1.5 bg-white px-2 py-1 border border-gray-200 rounded text-xs cursor-pointer hover:bg-gray-50 text-[#0d0d0d]">
                            <input 
                              type="checkbox" 
                              checked={offerForm.validDays.includes(day)}
                              onChange={(e) => {
                                const newDays = e.target.checked 
                                  ? [...offerForm.validDays, day] 
                                  : offerForm.validDays.filter(d => d !== day);
                                setOfferForm({ ...offerForm, validDays: newDays });
                              }}
                            />
                            {day.substring(0, 3)}
                          </label>
                        ))}
                      </div>
                    </label>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Valid From" required>
                    <CustomDatePickerFixed
                      value={offerForm.validFrom}
                      onChange={v => setOfferForm({ ...offerForm, validFrom: v })}
                      placeholder="Select Start Date"
                    />
                  </Field>
                  <Field label="Valid Until">
                    <CustomDatePickerFixed
                      value={offerForm.validUntil}
                      onChange={v => setOfferForm({ ...offerForm, validUntil: v })}
                      placeholder="Select End Date"
                    />
                  </Field>
                </div>
                <Field label="Status" icon={Power}>
                  <CustomSelect
                    value={offerForm.status}
                    onChange={v => setOfferForm({ ...offerForm, status: v })}
                    options={[
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" }
                    ]}
                  />
                </Field>
                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-8">

                  <button type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] bg-[var(--theme-primary)] hover:bg-gray-800 transition-colors">
                    {editItem ? 'Update Campaign' : 'Publish Campaign'}
                  </button>
                </div>
              </form>
            </motion.div>
        )}
      </AnimatePresence>

      {/* Discount Inline Form */}
      <AnimatePresence mode="wait">
        {viewState === 'discount_form' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="flex items-center gap-4 px-8 py-6 border-b border-gray-100 bg-gray-50/50">
              <button onClick={() => setViewState('list')} className="w-8 h-8 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors shadow-sm shrink-0">
                <ArrowLeft size={16} aria-hidden="true" />
              </button>
              <div>
                <h3 className="text-xl font-bold text-gray-900 tracking-tight">{editItem ? 'Edit Promo Code' : 'Create Promo Code'}</h3>
                <p className="text-sm text-gray-500 mt-1 font-medium">Create a SaaS-style discount or promo code for subscriptions.</p>
              </div>
            </div>
            <form onSubmit={handleSaveDiscount} className="p-8 space-y-6" noValidate>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Promo Code" icon={Tag} error={errors.code} required>
                    <input
                      type="text"
                      required
                      placeholder="e.g. SUMMER20"
                      className="uppercase"
                      value={discountForm.code}
                      onChange={e => {
                        setDiscountForm({ ...discountForm, code: e.target.value });
                        if (errors.code) setErrors({ ...errors, code: undefined });
                      }}
                    />
                  </Field>
                  <Field label="Campaign Name" icon={AlignLeft}>
                    <input
                      type="text"
                      placeholder="e.g. Summer Sale 2026"
                      value={discountForm.name}
                      onChange={e => setDiscountForm({ ...discountForm, name: e.target.value })}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Field label="Discount Type" icon={Layers}>
                    <CustomSelect
                      value={discountForm.type}
                      onChange={v => setDiscountForm({ ...discountForm, type: v })}
                      options={[
                        { value: "percentage", label: "Percentage Off (%)" },
                        { value: "fixed", label: "Fixed Amount (₹)" },
                        { value: "trial_ext", label: "Free Trial Extension (Days)" }
                      ]}
                    />
                  </Field>
                  <Field label="Value" icon={discountForm.type === 'percentage' ? Percent : (discountForm.type === 'trial_ext' ? Calendar : DollarSign)} error={errors.value} required>
                    <input
                      type="number"
                      step={discountForm.type === 'percentage' ? "0.1" : "1"}
                      required
                      value={discountForm.value}
                      onChange={e => {
                        setDiscountForm({ ...discountForm, value: e.target.value });
                        if (errors.value) setErrors({ ...errors, value: undefined });
                      }}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Field label="Applies To" icon={Gift}>
                    <CustomSelect
                      value={discountForm.appliesTo}
                      onChange={v => setDiscountForm({ ...discountForm, appliesTo: v })}
                      options={[
                        { value: "all_plans", label: "All Subscription Plans" },
                        { value: "specific_plans", label: "Specific Plans Only" },
                        { value: "addons", label: "Add-ons Only" }
                      ]}
                    />
                  </Field>
                  <Field label="Duration" icon={Activity}>
                    <CustomSelect
                      value={discountForm.duration}
                      onChange={v => setDiscountForm({ ...discountForm, duration: v })}
                      options={[
                        { value: "once", label: "Once (First Billing Cycle)" },
                        { value: "recurring", label: "Recurring (Multiple Cycles)" },
                        { value: "forever", label: "Forever (Lifetime)" }
                      ]}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Field label="Max Redemptions" icon={Search}>
                    <input
                      type="number"
                      placeholder="Leave blank for unlimited"
                      value={discountForm.maxRedemptions}
                      onChange={e => setDiscountForm({ ...discountForm, maxRedemptions: e.target.value })}
                    />
                  </Field>
                  <Field label="Status" icon={Power}>
                    <CustomSelect
                      value={discountForm.status}
                      onChange={v => setDiscountForm({ ...discountForm, status: v })}
                      options={[
                        { value: "active", label: "Active" },
                        { value: "inactive", label: "Inactive" }
                      ]}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Field label="Valid From" error={errors.validFrom} required>
                    <CustomDatePickerFixed
                      value={discountForm.validFrom}
                      onChange={v => {
                        setDiscountForm({ ...discountForm, validFrom: v });
                        if (errors.validFrom) setErrors({ ...errors, validFrom: undefined });
                      }}
                      placeholder="Start Date"
                    />
                  </Field>
                  <Field label="Valid Until" error={errors.validUntil}>
                    <CustomDatePickerFixed
                      value={discountForm.validUntil}
                      onChange={v => setDiscountForm({ ...discountForm, validUntil: v })}
                      placeholder="End Date"
                    />
                  </Field>
                </div>

                <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 mt-8">
                  <button type="submit" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] bg-[var(--theme-primary)] hover:bg-gray-800 transition-colors shadow-sm">
                    {editItem ? 'Update Promo Code' : 'Create Promo Code'}
                  </button>
                </div>
              </form>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
